import { yupResolver } from "@hookform/resolvers/yup";
import _ from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import * as yup from "yup";
import type { EditableHouseholdTrading } from "../../../../api/src/households-base/households-base.service";
import type { FirmsController } from "../../../../api/src/firms/firms.controller";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";
import Loading from "../../Loading";
import { NotificationContext } from "../../Notifications";
import { modelContainsCashSplit } from "../../cash/lib";
import DateInput from "../../components/DateInput";
import FormError from "../../components/FormError";
import FormFieldError from "../../components/FormFieldError";
import LabelValuePair from "../../components/LabelValuePair";
import ModelLink from "../../components/ModelLink";
import SubmitButton from "../../components/SubmitButton";
import { useAuthenticatedFetch } from "../../lib/api";
import { InlineError, displayAccountName, naLabel } from "../../lib/display";
import {
  getSchemaFieldLabel,
  idOptionalSelectorSchema,
  securityRestrictionSchema,
  securityRestrictionTreatmentSchema,
  undefinedNumberSchema,
} from "../../lib/forms";
import { formatCurrencyComponent } from "../../lib/numbers";
import { useQueryModel, useQueryModels } from "../../models/lib";
import RebalanceExecutedTable from "../../rebalances/RebalancesExecutedTable";
import AccountLotSelectionModal from "./AccountLotSelectionModal";
import HouseholdAssetAllocationChart from "./HouseholdAssetAllocationChart";
import { HouseholdContext } from "./HouseholdInfo";
import HouseholdTradingHoldingsTable from "./HouseholdTradingHoldingsTable";
import { useUpdateHouseholdModelTrading } from "./lib";
import AccountModelSummary from "./AccountModelSummary";
import { HouseholdWithAccounts } from "../../../../api/src/households/households.service";

// The api sends these fields as explicit null rather than omitting them
// (APP-1535). yup 1.x refuses a null against a bare schema, so the resolver
// would reject every household that has one and handleSubmit would never
// fire. An empty control still means "none"; it now reads as null instead
// of an absent key, and the route writes the same NULL.
const modelAssignmentStrategySchema = yup
  .string()
  .oneOf(["fallback", "preference", "completion", "soft_completion"] as const)
  .optional()
  .nullable()
  .label("Model Assignment Strategy");

// The same holds for the two capital gains budgets carried on each account in
// the nested `accounts` map below. The map is reset with whole Account objects,
// so an unset budget column now arrives as null rather than as an absent key.
const nullableBudgetSchema = yup
  .number()
  .transform((value, originalValue) =>
    typeof originalValue === "undefined" ||
    originalValue === null ||
    originalValue === ""
      ? null
      : value,
  )
  .defined()
  .nullable()
  .default(null);

const schema = yup.object({
  assignedModelId: idOptionalSelectorSchema.label("Assigned Model"),
  modelAssignmentStrategy: modelAssignmentStrategySchema,
  securityRestrictions: securityRestrictionSchema.required(),
  rebalanceWeightLimit: undefinedNumberSchema
    .min(0)
    .max(100)
    .label("Trade Proposal Weight Limit"),
  toleranceRiskPercent: yup
    .number()
    .min(0)
    .max(100)
    .nullable()
    .defined()
    .label("Risk Tolerance"),
  toleranceAssetClassPercent: yup
    .number()
    .min(0)
    .max(100)
    .nullable()
    .defined()
    .label("Asset Class Tolerance"),
  toleranceSecurityPercent: yup
    .number()
    .min(0)
    .max(100)
    .nullable()
    .defined()
    .label("Security Tolerance"),
  rebalancePeriod: yup
    .string()
    .required()
    .oneOf(["d", "w", "m", "q", "y"])
    .label("Trade Proposal Recurrence"),
  rebalancePeriodStartDate: yup
    .date()
    .required()
    .label("Trade Proposal Recurrence Start Date"),
  lotSelectionMethod: yup.string().optional().label("Lot Selection Method"),
  holdings: yup
    .array()
    .required()
    .of(
      yup.object({
        accountId: yup.number().required(),
        restrictSecurity: yup.bool().required(),
        symbol: yup.string().required(),
        treatment: securityRestrictionTreatmentSchema,
      }),
    ),
  accounts: yup.lazy((obj) =>
    yup.object(
      _.mapValues(obj, () =>
        yup.object({
          accountId: yup.number().required(),
          assignedModelId: idOptionalSelectorSchema,
          capitalGainsBudgetST: nullableBudgetSchema.min(0),
          capitalGainsBudgetLT: nullableBudgetSchema.min(0),
        }),
      ),
    ),
  ),
});

const HouseholdModelTrading = ({
  household: propHousehold,
  isEditable = true,
}: {
  household?: HouseholdWithAccounts;
  isEditable?: boolean;
}) => {
  const { household: contextHousehold } = useContext(HouseholdContext);
  const household = propHousehold ?? contextHousehold;
  const householdId = household?.id ?? 0;
  const householdIdStr = householdId.toString();

  // State for inherit from firm checkboxes
  const [inheritRiskTolerance, setInheritRiskTolerance] = useState(false);
  const [inheritAssetClassTolerance, setInheritAssetClassTolerance] =
    useState(false);
  const [inheritSecurityTolerance, setInheritSecurityTolerance] =
    useState(false);
  const [inheritModelAssignmentStrategy, setInheritModelAssignmentStrategy] =
    useState(false);

  // State for account lot selection modal
  const [showAccountLotSelectionModal, setShowAccountLotSelectionModal] =
    useState(false);

  // Fetch firm data for displaying in placeholders
  const { data: dataFirm } =
    useAuthenticatedFetch<
      SerializedObject<UnpackResponse<FirmsController["getCurrentFirm"]>>
    >("/firm/current");
  const firm = useMemo(
    () => (typeof dataFirm?.data === "undefined" ? undefined : dataFirm.data),
    [dataFirm?.data],
  );

  const form = useForm<EditableHouseholdTrading>({
    mode: "onBlur",
    resolver: yupResolver(schema) as any,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    control,
  } = form;

  const accounts = useMemo(() => household?.accounts, [household?.accounts]);
  const holdings = useMemo(
    () => (accounts ?? []).flatMap((account) => account.holdings ?? []),
    [accounts],
  );

  useEffect(() => {
    if (
      typeof household !== "undefined" &&
      typeof holdings !== "undefined" &&
      typeof accounts !== "undefined"
    ) {
      const accountsWithId = accounts.map((account) => ({
        ...account,
        accountId: account.id,
      }));
      const accountsMap = _.keyBy(accountsWithId, "id");

      // Initialize inherit state based on null values from database
      setInheritRiskTolerance(household.toleranceRiskPercent === null);
      setInheritAssetClassTolerance(
        household.toleranceAssetClassPercent === null,
      );
      setInheritSecurityTolerance(household.toleranceSecurityPercent === null);
      setInheritModelAssignmentStrategy(
        household.modelAssignmentStrategy == null,
      );

      reset({
        assignedModelId: household.assignedModelId,
        modelAssignmentStrategy: household.modelAssignmentStrategy,
        securityRestrictions: household.securityRestrictions,
        rebalanceWeightLimit: household.rebalanceWeightLimit,
        rebalancePeriod: household.rebalancePeriod,
        rebalancePeriodStartDate: household.rebalancePeriodStartDate,
        toleranceRiskPercent: household.toleranceRiskPercent,
        toleranceAssetClassPercent: household.toleranceAssetClassPercent,
        toleranceSecurityPercent: household.toleranceSecurityPercent,
        lotSelectionMethod:
          household.lotSelectionMethod || firm?.lotSelectionMethod || "HIFO",
        holdings: holdings.map((holding) => {
          const symbol = holding.security?.identifier ?? "";
          const account = accountsMap[holding.accountId];
          const securityRestriction = account?.securityRestrictions.find(
            (restriction) => restriction.symbol === symbol,
          );
          const restrictSecurity = typeof securityRestriction !== "undefined";

          return {
            ...holding,
            symbol,
            restrictSecurity,
            treatment: securityRestriction?.treatment ?? "sell_no_tax",
          };
        }),
        accounts: accountsMap,
      });
    }
  }, [accounts, holdings, household, reset]);

  const {
    isPending: isPendingModels,
    isError: isErrorModels,
    data: dataModels,
  } = useQueryModels();

  const modelOptions = [
    <option key={null} value="">
      None
    </option>,
    ...(dataModels ?? [])
      .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
      .map((model) => (
        <option key={model.id} value={model.id}>
          {model.name}
        </option>
      )),
  ];

  // R6 (doc-cash-split-01a05d5f47df200b): a model containing the cash split
  // cannot be assigned to an account configured with that cash split. The model
  // chosen here applies to the household's accounts, so the collision is
  // against any one of them whose cash split the model contains.
  //
  // The selected model is fetched one at a time through useQueryModel rather
  // than read out of useQueryModels above: getAllModels returns
  // primarySecurities: [] for every model by design
  // (api/src/models/models.service.ts), so a check built on dataModels would
  // silently never fire.
  //
  // The select hands back a string, and an unassigned model arrives as "" or
  // undefined; both coerce to something that is not a positive id, so nothing
  // is queried until a real model is chosen.
  const watchedAssignedModelId = Number(form.watch("assignedModelId"));
  const selectedModelId =
    watchedAssignedModelId > 0 ? watchedAssignedModelId : undefined;

  const { data: selectedModel } = useQueryModel(selectedModelId ?? -1, {
    enabled: typeof selectedModelId !== "undefined",
  });

  // Each account carries its own cash setting on the household record
  // (api/src/households/households.service.ts getWithAccounts), so naming the
  // account alongside the symbol costs no extra request.
  //
  // Only accounts with no model of their own are collected
  // (an-decision-01a0ef652cbc0922): an account with its own model does not
  // take the household's, so the model chosen here cannot collide for it.
  // `assignedModelId` is the account's own `model_id`
  // (api/src/accounts/accounts.service.ts), and the server's household check
  // applies the same filter (api/src/lib/cash-split.ts
  // HOUSEHOLD_ID_PREDICATE).
  const cashSplitCollisions = (accounts ?? [])
    .filter(
      (account) =>
        account.assignedModelId == null &&
        modelContainsCashSplit(
          selectedModel?.primarySecurities,
          account.cashSettings?.cashSplitSymbol,
        ),
    )
    .map(
      (account) =>
        `${account.cashSettings?.cashSplitSymbol?.trim()}, the Cash Split of account ${displayAccountName(
          account.displayName,
          account.displayNumber,
        )}`,
    );

  const cashSplitRefusal =
    typeof selectedModel === "undefined" || cashSplitCollisions.length === 0
      ? undefined
      : `Model ${selectedModel.name} contains ${cashSplitCollisions.join(
          "; ",
        )}. A model containing the cash split cannot be assigned to this household.`;

  const { mutateAsync: updateHousehold } =
    useUpdateHouseholdModelTrading(householdId);

  const notificationContext = useContext(NotificationContext);

  // Handler functions for inherit checkboxes
  const handleInheritRiskTolerance = useCallback((checked: boolean) => {
    setInheritRiskTolerance(checked);
  }, []);

  const handleInheritAssetClassTolerance = useCallback((checked: boolean) => {
    setInheritAssetClassTolerance(checked);
  }, []);

  const handleInheritSecurityTolerance = useCallback((checked: boolean) => {
    setInheritSecurityTolerance(checked);
  }, []);

  const handleInheritModelAssignmentStrategy = useCallback(
    (checked: boolean) => {
      setInheritModelAssignmentStrategy(checked);
    },
    [],
  );

  const onSubmit = useCallback(
    async (data: EditableHouseholdTrading) => {
      // The refusal is rendered beside the selector for as long as the
      // collision holds, so a save that stops here has already said why.
      if (typeof cashSplitRefusal !== "undefined") {
        return;
      }

      try {
        // Convert inherit state to null values for submission
        const submitData = {
          ...data,
          toleranceRiskPercent: inheritRiskTolerance
            ? null
            : data.toleranceRiskPercent,
          toleranceAssetClassPercent: inheritAssetClassTolerance
            ? null
            : data.toleranceAssetClassPercent,
          toleranceSecurityPercent: inheritSecurityTolerance
            ? null
            : data.toleranceSecurityPercent,
          modelAssignmentStrategy: inheritModelAssignmentStrategy
            ? null
            : data.modelAssignmentStrategy,
        };
        await updateHousehold(submitData as EditableHouseholdTrading);

        notificationContext.pushNotification({
          id: `household-${householdId}`,
          header: "Household Updated",
          body: `${household?.name} updated`,
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save household", err);
        notificationContext.pushNotification({
          id: `household-${householdId}`,
          header: "Failed to Save Household",
          body: `${household?.name} was not saved`,
          variant: "danger",
        });
      }
    },
    [
      cashSplitRefusal,
      household?.name,
      householdId,
      notificationContext,
      updateHousehold,
      inheritRiskTolerance,
      inheritAssetClassTolerance,
      inheritSecurityTolerance,
      inheritModelAssignmentStrategy,
    ],
  );

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xl={6}>
            <h3>Model Details</h3>
            <LabelValuePair
              className="mb-3"
              label={getSchemaFieldLabel(schema.fields.assignedModelId)}
              value={
                <>
                  {isPendingModels ? (
                    <Loading />
                  ) : isErrorModels ? (
                    <InlineError>There was an error loading models</InlineError>
                  ) : (
                    <>
                      <ModelLink modelId={form.watch("assignedModelId")} />
                      <Form.Select
                        placeholder="Select assigned model"
                        {...register("assignedModelId")}
                        disabled={!isEditable}
                      >
                        {modelOptions}
                      </Form.Select>
                    </>
                  )}
                  <FormFieldError field={errors.assignedModelId} />
                  {typeof cashSplitRefusal === "undefined" ? null : (
                    <FormError message={cashSplitRefusal} />
                  )}
                </>
              }
              orientation="horizontal"
              widths={{ label: { lg: 6 } }}
              plaintext={isErrorModels}
            />
            <LabelValuePair
              className="mb-3"
              label={getSchemaFieldLabel(schema.fields.modelAssignmentStrategy)}
              value={
                <>
                  <Form.Check
                    className="mb-2"
                    type="checkbox"
                    id="inherit-model-assignment-strategy"
                    label="Inherit from firm"
                    checked={inheritModelAssignmentStrategy}
                    onChange={(e) =>
                      handleInheritModelAssignmentStrategy(e.target.checked)
                    }
                    disabled={!isEditable}
                  />
                  <Form.Select
                    {...(inheritModelAssignmentStrategy
                      ? {}
                      : register("modelAssignmentStrategy"))}
                    disabled={!isEditable || inheritModelAssignmentStrategy}
                    value={
                      inheritModelAssignmentStrategy
                        ? ""
                        : (form.watch("modelAssignmentStrategy") ?? "")
                    }
                  >
                    <option value="fallback">Fallback</option>
                    <option value="completion">Completion</option>
                    <option value="soft_completion">Soft Completion</option>
                  </Form.Select>
                  {inheritModelAssignmentStrategy && (
                    <Form.Text className="text-muted">
                      Using firm default:{" "}
                      {firm?.modelAssignmentStrategy ?? "fallback"}
                    </Form.Text>
                  )}
                  {(inheritModelAssignmentStrategy
                    ? firm?.modelAssignmentStrategy
                    : form.watch("modelAssignmentStrategy")) === "preference" &&
                    form.watch("assignedModelId") && (
                      <Form.Text className="text-warning d-block mt-1">
                        Assigned model ignored for Preference
                      </Form.Text>
                    )}
                </>
              }
              orientation="horizontal"
              widths={{ label: { lg: 6 } }}
            />
            <AccountModelSummary
              accounts={household?.accounts ?? []}
              models={dataModels ?? []}
            />
            <h5>Tolerance Defaults</h5>
            <Row className="mb-3">
              <Col lg={4}>
                <LabelValuePair
                  label={getSchemaFieldLabel(
                    schema.fields.toleranceRiskPercent,
                  )}
                  value={
                    <>
                      <Form.Check
                        className="mb-2"
                        type="checkbox"
                        id="inherit-risk-tolerance"
                        label="Inherit from firm"
                        checked={inheritRiskTolerance}
                        onChange={(e) =>
                          handleInheritRiskTolerance(e.target.checked)
                        }
                        disabled={!isEditable}
                      />
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          step={0.0001}
                          {...(inheritRiskTolerance
                            ? {}
                            : register("toleranceRiskPercent"))}
                          disabled={!isEditable || inheritRiskTolerance}
                          value={
                            inheritRiskTolerance
                              ? ""
                              : (form.watch("toleranceRiskPercent") ?? "")
                          }
                          placeholder={
                            inheritRiskTolerance
                              ? `${firm?.toleranceRiskPercent ?? 0}% (from firm)`
                              : undefined
                          }
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </>
                  }
                  orientation="vertical"
                />
              </Col>
              <Col lg={4}>
                <LabelValuePair
                  label={getSchemaFieldLabel(
                    schema.fields.toleranceAssetClassPercent,
                  )}
                  value={
                    <>
                      <Form.Check
                        className="mb-2"
                        type="checkbox"
                        id="inherit-asset-class-tolerance"
                        label="Inherit from firm"
                        checked={inheritAssetClassTolerance}
                        onChange={(e) =>
                          handleInheritAssetClassTolerance(e.target.checked)
                        }
                        disabled={!isEditable}
                      />
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          step={0.0001}
                          {...(inheritAssetClassTolerance
                            ? {}
                            : register("toleranceAssetClassPercent"))}
                          disabled={!isEditable || inheritAssetClassTolerance}
                          value={
                            inheritAssetClassTolerance
                              ? ""
                              : (form.watch("toleranceAssetClassPercent") ?? "")
                          }
                          placeholder={
                            inheritAssetClassTolerance
                              ? `${firm?.toleranceAssetClassPercent ?? 0}% (from firm)`
                              : undefined
                          }
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </>
                  }
                  orientation="vertical"
                />
              </Col>
              <Col lg={4}>
                <LabelValuePair
                  label={getSchemaFieldLabel(
                    schema.fields.toleranceSecurityPercent,
                  )}
                  value={
                    <>
                      <Form.Check
                        className="mb-2"
                        type="checkbox"
                        id="inherit-security-tolerance"
                        label="Inherit from firm"
                        checked={inheritSecurityTolerance}
                        onChange={(e) =>
                          handleInheritSecurityTolerance(e.target.checked)
                        }
                        disabled={!isEditable}
                      />
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          step={0.0001}
                          {...(inheritSecurityTolerance
                            ? {}
                            : register("toleranceSecurityPercent"))}
                          disabled={!isEditable || inheritSecurityTolerance}
                          value={
                            inheritSecurityTolerance
                              ? ""
                              : (form.watch("toleranceSecurityPercent") ?? "")
                          }
                          placeholder={
                            inheritSecurityTolerance
                              ? `${firm?.toleranceSecurityPercent ?? 0}% (from firm)`
                              : undefined
                          }
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </>
                  }
                  orientation="vertical"
                />
              </Col>
            </Row>

            <Row className="mt-5 mb-3">
              <Col lg={6}>
                <label className="form-label col-form-label col-lg-6">
                  Lot Selection
                </label>
              </Col>
              <Col lg={6} className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  onClick={() => setShowAccountLotSelectionModal(true)}
                >
                  Configure Settings
                </Button>
              </Col>
            </Row>

            <LabelValuePair
              className="mb-3 mt-5"
              label={getSchemaFieldLabel(schema.fields.rebalanceWeightLimit)}
              value={
                <InputGroup>
                  <Form.Control
                    type="number"
                    min={0}
                    max={100}
                    {...register("rebalanceWeightLimit")}
                    disabled={!isEditable}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              }
              orientation="horizontal"
              widths={{ label: { lg: 6 } }}
            />
            <LabelValuePair
              className="mb-3"
              label={getSchemaFieldLabel(schema.fields.rebalancePeriod)}
              value={
                <Form.Select
                  placeholder="Select rebalance period"
                  {...register("rebalancePeriod")}
                  disabled={!isEditable}
                >
                  <option value="d">Daily</option>
                  <option value="w">Weekly</option>
                  <option value="m">Monthly</option>
                  <option value="q">Quarterly</option>
                  <option value="y">Yearly</option>
                </Form.Select>
              }
              orientation="horizontal"
              widths={{ label: { lg: 6 } }}
            />
            <LabelValuePair
              className="mb-3"
              label={getSchemaFieldLabel(
                schema.fields.rebalancePeriodStartDate,
              )}
              value={
                <DateInput
                  name="rebalancePeriodStartDate"
                  control={control}
                  isEditable={isEditable}
                />
              }
              orientation="horizontal"
              widths={{ label: { lg: 6 } }}
            />
            <LabelValuePair
              label="Realized Gains YTD"
              value={formatCurrencyComponent(
                (household?.accounts ?? []).reduce(
                  (sum, account) =>
                    sum + (account.performanceYTD?.realizedGainLoss ?? 0),
                  0,
                ),
              )}
              orientation="horizontal"
              widths={{ label: { lg: 6 } }}
              plaintext
            />
            <LabelValuePair
              label="Last Trade Proposal Date"
              value={
                typeof household?.lastRebalanceDate === "undefined" ||
                household.lastRebalanceDate === null
                  ? naLabel
                  : household.lastRebalanceDate.toLocaleDateString()
              }
              orientation="horizontal"
              widths={{ label: { lg: 6 } }}
              plaintext
            />
            <LabelValuePair
              label="Pending Trade Proposal"
              value={
                typeof household?.latestRebalanceDate === "undefined" ||
                household.latestRebalanceDate === null ? (
                  naLabel
                ) : (
                  <Link to={`/rebalances/${household.latestRebalanceId}`}>
                    {household.latestRebalanceDate.toLocaleDateString()}
                  </Link>
                )
              }
              orientation="horizontal"
              widths={{ label: { lg: 6 } }}
              plaintext
            />
          </Col>
          <Col xl={6}>
            <h3>Allocation Details</h3>
            <HouseholdAssetAllocationChart householdId={householdId} />
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            {isEditable && <SubmitButton isSubmitting={isSubmitting} />}
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            <h3>Holdings</h3>
            <HouseholdTradingHoldingsTable
              holdings={holdings}
              accounts={accounts}
              isEditable={isEditable}
            />
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            {isEditable && <SubmitButton isSubmitting={isSubmitting} />}
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            <h3>Trade Proposal History</h3>
            <RebalanceExecutedTable
              householdId={householdIdStr}
              name="Trade Proposal History"
            />
          </Col>
        </Row>
      </Form>

      <AccountLotSelectionModal
        show={showAccountLotSelectionModal}
        onHide={() => setShowAccountLotSelectionModal(false)}
        householdId={householdId}
        accounts={(household?.accounts ?? []).map((account) => ({
          accountId: account.id,
          value: account.accountBalance ?? 0,
          unrealizedGainLoss: account.unrealizedGainLoss ?? 0,
          realizedGainLoss: account.performanceYTD?.realizedGainLoss ?? 0,
        }))}
        onSave={() => {
          setShowAccountLotSelectionModal(false);
          // Note: The modal handles its own data updates and notifications
        }}
      />
    </FormProvider>
  );
};

export default HouseholdModelTrading;
